import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { 
  LayoutDashboard, 
  Building2, 
  Users, 
  FileText, 
  UserPlus,
  Wallet,
  Settings,
  LogOut,
  CreditCard,
  Lock,
  GraduationCap,
  Download,
  ExternalLink,
  QrCode,
  Eye,
  Trash2,
  Menu,
  X,
  ChevronRight
} from 'lucide-react';

export const Sidebar = ({ children }) => {
  const { user, logout, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [downloadCenter, setDownloadCenter] = useState(null);
  const [paymentQR, setPaymentQR] = useState(null);
  const [showQRPopup, setShowQRPopup] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

  useEffect(() => {
    fetchDownloadCenter();
    fetchPaymentQR();
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Close mobile menu on window resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsMobileMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchDownloadCenter = async () => {
    try {
      const response = await axios.get(`${API_URL}/download-center`, {
        headers: getAuthHeaders()
      });
      setDownloadCenter(response.data);
    } catch (error) {
      console.error('Failed to fetch download center');
    }
  };

  const fetchPaymentQR = async () => {
    try {
      const response = await axios.get(`${API_URL}/payment-qr`, {
        headers: getAuthHeaders()
      });
      setPaymentQR(response.data);
    } catch (error) {
      console.error('Failed to fetch payment QR');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleDownloadCenterClick = () => {
    if (user?.role === 'admin' || user?.role === 'sub_admin') {
      navigate('/download-center');
    } else if (downloadCenter?.url) {
      window.open(downloadCenter.url, '_blank');
    }
  };

  const handlePaymentQRClick = () => {
    if (user?.role === 'admin' || user?.role === 'sub_admin') {
      navigate('/payment-qr');
    } else {
      setShowQRPopup(true);
    }
  };

  const adminLinks = [
    { path: '/', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/colleges', icon: Building2, label: 'Colleges' },
    { path: '/agencies', icon: Users, label: 'Agencies' },
    { path: '/applications', icon: FileText, label: 'Applications' },
    { path: '/trash', icon: Trash2, label: 'Trash' },
    { path: '/offline-payments', icon: CreditCard, label: 'Offline Payments' },
    { path: '/users', icon: UserPlus, label: 'User Management' },
    { path: '/payment-details', icon: Wallet, label: 'Payment Details' },
    { path: '/escalation-matrix', icon: Settings, label: 'Escalation Matrix' },
    { path: '/dropdown-controls', icon: Settings, label: 'Dropdown Controls' },
    { path: '/change-password', icon: Lock, label: 'Change Password' },
  ];

  const agencyLinks = [
    { path: '/', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/apply', icon: FileText, label: 'Apply Admission' },
    { path: '/applications', icon: FileText, label: 'Applications' },
    { path: '/offline-payments', icon: CreditCard, label: 'Offline Payments' },
    { path: '/change-password', icon: Lock, label: 'Change Password' },
  ];

  const masterAgencyLinks = [
    { path: '/', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/agencies', icon: Users, label: 'My Agencies' },
    { path: '/applications', icon: FileText, label: 'Applications' },
    { path: '/offline-payments', icon: CreditCard, label: 'Offline Payments' },
    { path: '/users', icon: UserPlus, label: 'User Management' },
    { path: '/change-password', icon: Lock, label: 'Change Password' },
  ];

  const links = user?.role === 'agency' ? agencyLinks : 
                user?.role === 'master_agency' ? masterAgencyLinks : adminLinks;

  const SidebarContent = ({ isMobile = false }) => (
    <>
      {/* Logo & User Info */}
      <div className="p-4 lg:p-6 border-b border-purple-900/30">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-violet-500 to-purple-600 rounded-lg shadow-lg shadow-purple-500/20">
            <GraduationCap className="w-5 h-5 lg:w-6 lg:h-6 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg lg:text-xl font-bold text-white truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>
              College Sarathi
            </h1>
          </div>
          {isMobile && (
            <button 
              onClick={() => setIsMobileMenuOpen(false)}
              className="p-2 rounded-lg hover:bg-white/10 text-slate-400 lg:hidden"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
        <div className="mt-4 pt-4 border-t border-purple-900/30">
          <p className="text-sm text-slate-400 truncate">{user?.name}</p>
          <span className="inline-block mt-1 px-2 py-1 text-xs rounded bg-purple-900/40 text-purple-300">
            {user?.role?.replace('_', ' ').toUpperCase()}
          </span>
        </div>
      </div>

      {/* Navigation Links */}
      <nav className="flex-1 py-4 overflow-y-auto">
        {links.map((link) => {
          const Icon = link.icon;
          const isActive = location.pathname === link.path;
          return (
            <button
              key={link.path}
              onClick={() => navigate(link.path)}
              data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
              className={`sidebar-link w-full flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-lg mx-2 my-1 transition-all duration-200 ${
                isActive 
                  ? 'bg-gradient-to-r from-violet-600/30 to-purple-600/30 text-white border-l-2 border-violet-400' 
                  : 'text-slate-400 hover:text-white hover:bg-white/5'
              }`}
              style={{ width: 'calc(100% - 16px)' }}
            >
              <Icon className="w-5 h-5 flex-shrink-0" />
              <span className="truncate">{link.label}</span>
              {isActive && <ChevronRight className="w-4 h-4 ml-auto opacity-60" />}
            </button>
          );
        })}
      </nav>

      {/* Footer Actions */}
      <div className="p-4 border-t border-purple-900/30 space-y-2">
        {/* Download Center Button */}
        <button
          onClick={handleDownloadCenterClick}
          data-testid="download-center-button"
          className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-lg bg-gradient-to-r from-violet-600/20 to-purple-600/20 hover:from-violet-600/30 hover:to-purple-600/30 text-purple-300 transition-colors border border-purple-500/30"
        >
          <Download className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1 text-left truncate">Download Center</span>
          {(user?.role !== 'admin' && user?.role !== 'sub_admin') && downloadCenter?.url && (
            <ExternalLink className="w-4 h-4 opacity-60 flex-shrink-0" />
          )}
          {(user?.role === 'admin' || user?.role === 'sub_admin') && (
            <Settings className="w-4 h-4 opacity-60 flex-shrink-0" />
          )}
        </button>

        {/* Payment QR Button */}
        <button
          onClick={handlePaymentQRClick}
          data-testid="payment-qr-button"
          className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-lg bg-gradient-to-r from-emerald-600/20 to-teal-600/20 hover:from-emerald-600/30 hover:to-teal-600/30 text-emerald-300 transition-colors border border-emerald-500/30"
        >
          <QrCode className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1 text-left truncate">Payment QR</span>
          {(user?.role !== 'admin' && user?.role !== 'sub_admin') && (
            <Eye className="w-4 h-4 opacity-60 flex-shrink-0" />
          )}
          {(user?.role === 'admin' || user?.role === 'sub_admin') && (
            <Settings className="w-4 h-4 opacity-60 flex-shrink-0" />
          )}
        </button>

        <button
          onClick={handleLogout}
          data-testid="logout-button"
          className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-lg hover:bg-red-500/10 hover:text-red-400 text-slate-400 transition-colors"
        >
          <LogOut className="w-5 h-5 flex-shrink-0" />
          <span className="truncate">Logout</span>
        </button>
      </div>
    </>
  );

  return (
    <div className="flex min-h-screen bg-slate-50">
      {/* Mobile Header */}
      <div className="fixed top-0 left-0 right-0 z-40 lg:hidden bg-[#0f0a1a] border-b border-purple-900/30">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-violet-500 to-purple-600 rounded-lg">
              <GraduationCap className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-lg font-bold text-white">College Sarathi</h1>
          </div>
          <button
            onClick={() => setIsMobileMenuOpen(true)}
            className="p-2 rounded-lg hover:bg-white/10 text-slate-300"
            data-testid="mobile-menu-button"
          >
            <Menu className="w-6 h-6" />
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/60 z-40 lg:hidden backdrop-blur-sm"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <aside 
        className={`fixed left-0 top-0 h-full w-72 bg-[#0f0a1a] text-slate-300 flex flex-col z-50 transform transition-transform duration-300 ease-in-out lg:hidden ${
          isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <SidebarContent isMobile={true} />
      </aside>

      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex fixed left-0 top-0 h-full w-64 xl:w-72 bg-[#0f0a1a] text-slate-300 flex-col border-r border-purple-900/30 z-50">
        <SidebarContent />
      </aside>

      {/* Main Content */}
      <main className="flex-1 lg:ml-64 xl:ml-72 pt-16 lg:pt-0">
        <div className="p-4 sm:p-6 lg:p-8 xl:p-12">
          {children}
        </div>
      </main>

      {/* Payment QR Popup for Agency/Master Agency */}
      <Dialog open={showQRPopup} onOpenChange={setShowQRPopup}>
        <DialogContent className="sm:max-w-md mx-4">
          <DialogHeader>
            <DialogTitle className="text-center">Payment QR Code</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center justify-center p-4">
            {paymentQR?.image_data ? (
              <img 
                src={paymentQR.image_data} 
                alt="Payment QR Code" 
                className="max-w-full max-h-[400px] rounded-lg border border-slate-200"
              />
            ) : (
              <div className="text-center py-8 text-slate-500">
                <QrCode className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p>Payment QR not available yet.</p>
                <p className="text-sm">Please contact Admin.</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
